import React from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip as ChartTooltip,
  Legend,
  TooltipItem,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import { InterpreterInfoTooltip } from './InterpreterInfoTooltip'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTooltip, Legend)

/** Visily App.tsx earnings section */
const SHADOW_SM = '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
const TEXT_MUTED = '#565d6d'
const TEXT_PRIMARY = '#171a1f'
const BORDER_CARD = '#dee1e6'
const CHART_FONT = "'Lexend', sans-serif"

export type InterpreterEarningsChartData = {
  title: string
  tooltipText?: string
  subtitle: string
  totalLabel: string
  totalValue: string
  labels: string[]
  values: number[]
  legendLabel: string
  yPrefix?: string
}

type InterpreterEarningsChartProps = {
  chart: InterpreterEarningsChartData
}

export function InterpreterEarningsChart({ chart }: InterpreterEarningsChartProps) {
  const data = {
    labels: chart.labels,
    datasets: [
      {
        label: chart.legendLabel,
        data: chart.values,
        borderColor: '#a3a242',
        backgroundColor: 'transparent',
        tension: 0.35,
        pointRadius: 4.5,
        pointHoverRadius: 5.5,
        pointBackgroundColor: '#a3a242',
        pointBorderColor: '#a3a242',
        borderWidth: 2,
        fill: false,
      },
    ],
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        bodyFont: { family: CHART_FONT, size: 12 },
        titleFont: { family: CHART_FONT, size: 12 },
        callbacks: {
          label: (ctx: TooltipItem<'line'>) =>
            `${chart.yPrefix ?? ''}${Number(ctx.parsed.y).toLocaleString()}`,
        },
      },
    },
    scales: {
      x: {
        display: true,
        grid: { display: false, drawBorder: false },
        ticks: { color: TEXT_MUTED, font: { size: 12, family: CHART_FONT } },
      },
      y: {
        display: true,
        grid: { color: 'rgba(230, 232, 236, 0.8)', borderDash: [5, 4] },
        beginAtZero: true,
        min: 0,
        ticks: {
          color: TEXT_MUTED,
          font: { size: 12, family: CHART_FONT },
          callback: (value: string | number) => `${chart.yPrefix ?? ''}${value}`,
        },
      },
    },
  }

  return (
    <section
      style={{
        backgroundColor: '#ffffff',
        border: `1px solid ${BORDER_CARD}`,
        borderRadius: 12,
        padding: 24,
        boxShadow: SHADOW_SM,
      }}
    >
      <div
        className='d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-4'
        style={{ marginBottom: 32 }}
      >
        <div>
          <div className='d-flex align-items-center gap-2'>
            <h3
              className='mb-0'
              style={{
                fontFamily: "'Lexend', sans-serif",
                fontSize: 24,
                fontWeight: 600,
                color: TEXT_PRIMARY,
              }}
            >
              {chart.title}
            </h3>
            {chart.tooltipText ? (
              <InterpreterInfoTooltip id='earnings-overview-tooltip' text={chart.tooltipText} />
            ) : null}
          </div>
          <p className='mb-0' style={{ marginTop: 4, fontSize: 14, color: TEXT_MUTED }}>
            {chart.subtitle}
          </p>
        </div>
        <div className='text-end'>
          <p className='mb-0' style={{ fontSize: 14, fontWeight: 500, color: TEXT_MUTED }}>
            {chart.totalLabel}
          </p>
          <p
            className='mb-0'
            style={{
              marginTop: 4,
              fontFamily: "'Lexend', sans-serif",
              fontSize: 30,
              fontWeight: 700,
              color: TEXT_PRIMARY,
            }}
          >
            {chart.totalValue}
          </p>
        </div>
      </div>

      <div style={{ height: 260 }}>
        <Line data={data} options={options} />
      </div>

      <div className='d-flex justify-content-center gap-4' style={{ marginTop: 16 }}>
        <div className='d-flex align-items-center gap-2'>
          <span
            className='d-inline-block rounded-circle flex-shrink-0'
            style={{ width: 10, height: 10, backgroundColor: '#a3a242' }}
          />
          <span style={{ fontSize: 14, color: TEXT_PRIMARY }}>{chart.legendLabel}</span>
        </div>
      </div>
    </section>
  )
}
